import { globSync } from "glob"
import path from "node:path"
import * as ts from "typescript"

import { mapToSupernovaComponent } from "./mappers/component.js"
import { defaultOptions, ModuleParser } from "./parser/index.js"
import { ParserOptions } from "./parser/types.js"
import { Component } from "./types.js"
import { getModuleEntryPath } from "./utils/get-module-exports-path.js"

type Input = {
  /**
   * Where to import components from within the package
   */
  importFrom: string

  /**
   * Root directory of the package where the import should resolve from
   */
  rootDir: string

  parserOptions?: ParserOptions
}

/**
 * Finds and parses a tsconfig.json file from a specific directory
 */
function findAndParseTsConfig(directory: string): ts.ParsedCommandLine | undefined {
  const tsconfigPath = path.join(directory, "tsconfig.json")

  const configFile = ts.readConfigFile(tsconfigPath, ts.sys.readFile)

  if (configFile.error) {
    return undefined
  }

  return ts.parseJsonConfigFileContent(configFile.config, ts.sys, directory, undefined, tsconfigPath)
}

async function getDefaultFiles(rootDir: string) {
  return globSync("**/*.{ts,tsx,js,jsx,mts,mjs,cts,cjs}", {
    absolute: true,
    cwd: rootDir,
    ignore: [
      "**/.supernova/**",
      "**/build/**",
      "**/dist/**",
      "**/node_modules/**",
      "**/*.d.ts",
      "**/*.d.mts",
      "**/*.d.cts",
    ],
  })
}

export async function analyzeComponents(input: Input): Promise<Component[]> {
  // Construct the module path
  const entryFilePath = await getModuleEntryPath(input.rootDir, input.importFrom)

  const tsConfig = findAndParseTsConfig(input.rootDir)

  const isNodeModuleImport = entryFilePath.includes("/node_modules/")

  // inlcude ts files defined in tsConfig. If not, include all ts and tsx files in the root dir
  const discoveredTsFiles =
    tsConfig?.fileNames.length && !isNodeModuleImport
      ? tsConfig.fileNames
      : await getDefaultFiles(isNodeModuleImport ? `${input.rootDir}/node_modules` : input.rootDir)
  const tsFiles = discoveredTsFiles.includes(entryFilePath) ? discoveredTsFiles : [entryFilePath, ...discoveredTsFiles]

  const program = ts.createProgram(tsFiles, {
    ...defaultOptions,
    ...(tsConfig?.options ?? {}),
    allowJs: true,
    checkJs: false,
  })

  const moduleParser = new ModuleParser(program, entryFilePath, input.parserOptions)
  const moduleComponentsInfo = moduleParser.parseModule()

  return moduleComponentsInfo.map(componentInfo => mapToSupernovaComponent(componentInfo, path.dirname(entryFilePath)))
}
